import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWorkTimer } from '../hooks/useWorkTimer';
import api from '../services/api';
import { useI18n } from '../i18n';
import FlextimeCard from '../components/FlextimeCard';

// Setzt hervorgehobenen Inhalt an die Stelle des {value}-Platzhalters.
const withStrong = (text, value) => {
  const [before, after] = text.split('{value}');
  return <>{before}<strong>{value}</strong>{after}</>;
};

export default function Dashboard() {
  const { user } = useAuth();
  const { t, locale } = useI18n();
  const { status, loading, refresh, formatMinutes } = useWorkTimer();
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');

  const handleCheckIn = async (location) => {
    setActionLoading(location);
    setError('');
    try {
      await api.post('/time/check-in', { work_location: location });
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || t('dashboard.error'));
    } finally {
      setActionLoading('');
    }
  };

  const handleCheckOut = async () => {
    setActionLoading('OUT');
    setError('');
    try {
      await api.post('/time/check-out', {});
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || t('dashboard.error'));
    } finally {
      setActionLoading('');
    }
  };

  const progressPct = status?.active
    ? Math.min(100, Math.round((status.elapsed_minutes / status.schedule_minutes) * 100))
    : 0;

  const progressColor = progressPct >= 100 ? 'bg-success' : progressPct >= 75 ? 'bg-warning' : 'bg-primary';

  return (
    <div className="row g-4">
      <div className="col-12">
        <h4><i className="bi bi-speedometer2 me-2" />{t('dashboard.title')}</h4>
        <p className="text-muted">{withStrong(t('dashboard.greeting', { name: '{value}' }), `${user?.first_name} ${user?.last_name}`)}</p>
      </div>

      {/* Timer Card */}
      <div className="col-md-6">
        <div className="card h-100">
          <div className="card-body text-center py-4">
            {loading ? (
              <div className="spinner-border text-primary" />
            ) : status?.active ? (
              <>
                <div className="mb-2">
                  <span className={`badge ${status.entry?.work_location === 'HOME_OFFICE' ? 'bg-info' : 'bg-success'} work-status-badge`}>
                    <i className={`bi ${status.entry?.work_location === 'HOME_OFFICE' ? 'bi-house' : 'bi-building'} me-1`} />
                    {status.entry?.work_location === 'HOME_OFFICE' ? t('time.homeOffice') : t('time.office')}
                  </span>
                </div>
                <div className="timer-display text-primary">{formatMinutes(status.elapsed_minutes)}</div>
                <div className="text-muted mb-3">{t('dashboard.workedOf', { hours: formatMinutes(status.schedule_minutes) })}</div>
                <div className="progress mb-3" style={{ height: 12 }}>
                  <div className={`progress-bar ${progressColor}`} style={{ width: `${progressPct}%` }} />
                </div>
                <div className="text-muted small mb-3">
                  {status.remaining_minutes > 0
                    ? withStrong(t('dashboard.remaining', { hours: '{value}' }), formatMinutes(status.remaining_minutes))
                    : <span className="text-success fw-bold">{t('dashboard.targetReached', { hours: formatMinutes(-status.remaining_minutes) })}</span>
                  }
                </div>
                <button className="btn btn-danger" onClick={handleCheckOut} disabled={!!actionLoading}>
                  {actionLoading === 'OUT' ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-door-open me-2" />}
                  {t('dashboard.checkOut')}
                </button>
              </>
            ) : (
              <>
                <i className="bi bi-clock text-muted" style={{ fontSize: '3rem' }} />
                <p className="text-muted mt-2 mb-3">{t('dashboard.notCheckedIn')}</p>
                <div className="d-flex gap-2 justify-content-center flex-wrap">
                  <button className="btn btn-success" onClick={() => handleCheckIn('OFFICE')} disabled={!!actionLoading}>
                    {actionLoading === 'OFFICE' ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-building me-2" />}
                    {t('time.office')}
                  </button>
                  <button className="btn btn-info text-white" onClick={() => handleCheckIn('HOME_OFFICE')} disabled={!!actionLoading}>
                    {actionLoading === 'HOME_OFFICE' ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-house me-2" />}
                    {t('time.homeOffice')}
                  </button>
                </div>
              </>
            )}
            {error && <div className="alert alert-danger mt-3 py-2">{error}</div>}
          </div>
        </div>
      </div>

      {/* Quick Info Cards */}
      <div className="col-md-6">
        <div className="row g-3">
          <div className="col-6">
            <div className="card text-center">
              <div className="card-body">
                <i className="bi bi-calendar-check fs-2 text-primary" />
                <div className="fw-bold mt-1">{t('dashboard.today')}</div>
                <div className="text-muted small">
                  {status?.entry?.check_in
                    ? t('dashboard.since', { time: new Date(status.entry.check_in).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) })
                    : t('dashboard.noEntry')}
                </div>
              </div>
            </div>
          </div>
          <div className="col-6">
            <div className="card text-center">
              <div className="card-body">
                <i className="bi bi-calendar3 fs-2 text-info" />
                <div className="fw-bold mt-1">{t('dashboard.month')}</div>
                <div className="text-muted small">{t('dashboard.toMonthView')}</div>
              </div>
            </div>
          </div>
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <i className="bi bi-sun fs-3 text-warning me-3" />
                  <div>
                    <div className="fw-bold">{t('dashboard.vacationOverview')}</div>
                    <div className="text-muted small">{t('dashboard.vacationHint')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="col-12">
        <FlextimeCard />
      </div>
    </div>
  );
}
